import { useState } from "react";
import { Link } from "react-router-dom";

const principles = [
  { icon: "ri-focus-3-line", title: "内容为本", desc: "坚持真实、有价值的内容推荐，用心挑选每一部值得阅读的韩漫。" },
  { icon: "ri-user-smile-line", title: "读者优先", desc: "从读者角度出发，榜单与分类都围绕「快速找到想看的漫画」而设计。" },
  { icon: "ri-refresh-line", title: "持续更新", desc: "追踪热门韩漫动态，及时收录新作与新章，让你不错过任何精彩。" },
  { icon: "ri-heart-3-line", title: "公正客观", desc: "以评分与读者口碑为依据呈现榜单，不偏不倚地展示作品真实水平。" },
];

const testimonials = [
  { name: "小鹿乱撞", avatar: "https://readdy.ai/api/search-image?query=Warm%20cute%20illustration%20avatar%20of%20a%20young%20girl%20with%20short%20hair%20and%20a%20gentle%20smile%2C%20peach%20and%20cream%20palette%2C%20flat%20cartoon%20style%2C%20clean%20background&width=200&height=200&seq=avatar-01&orientation=squarish", text: "在这里按「恋爱」题材一下就找到了好几部超甜的作品，完结推荐也超靠谱，不用自己瞎猜开坑了！" },
  { name: "芝士年糕", avatar: "https://readdy.ai/api/search-image?query=Warm%20cute%20illustration%20avatar%20of%20a%20young%20man%20with%20glasses%20and%20a%20friendly%20grin%2C%20teal%20and%20cream%20palette%2C%20flat%20cartoon%20style%2C%20clean%20background&width=200&height=200&seq=avatar-02&orientation=squarish", text: "奇幻分类做得很好，画风也都很对胃口。排行榜挺准的，基本没踩过雷。" },
  { name: "熬夜追番中", avatar: "https://readdy.ai/api/search-image?query=Warm%20cute%20illustration%20avatar%20of%20a%20girl%20with%20long%20hair%20and%20excited%20eyes%2C%20coral%20and%20cream%20palette%2C%20flat%20cartoon%20style%2C%20clean%20background&width=200&height=200&seq=avatar-03&orientation=squarish", text: "更新很及时，完结推荐让我一口气补了好几部高分神作，体验很不错，会继续关注。" },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<{ type: "idle" | "success" | "error"; msg: string }>({ type: "idle", msg: "" });
  const [sending, setSending] = useState(false);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormStatus({ type: "success", msg: "感谢你的留言，我们会尽快回复你！" });
      form.reset();
      return;
    }
    setSending(true);
    try {
      const payload = new URLSearchParams();
      data.forEach((v, k) => {
        if (k !== "website_alt" && v instanceof File) {
          payload.append(k, "Uncollectable");
        } else if (k !== "website_alt") {
          payload.append(k, String(v));
        }
      });
      const res = await fetch("https://readdy.ai/api/form/da296rlv7gurs51ef9ig", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message ?? parsed?.meta?.detail ?? "";
      const spam = serverMsg.toLowerCase().includes("spam");
      if (res.ok && parsed?.code === "OK" && !spam) {
        setFormStatus({ type: "success", msg: "感谢你的留言，我们会尽快回复你！" });
        form.reset();
      } else {
        setFormStatus({
          type: "error",
          msg: (parsed?.meta?.message || "提交失败，请稍后再试。"),
        });
      }
    } catch {
      setFormStatus({ type: "error", msg: "网络异常，提交失败，请稍后再试。" });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20cozy%20Korean%20webtoon%20style%20illustration%20of%20a%20reading%20corner%20with%20stacks%20of%20comic%20books%2C%20plants%20and%20a%20cup%20of%20tea%2C%20peach%20and%20cream%20palette%2C%20soft%20morning%20light%2C%20high%20detail%2C%20clean%20artwork&width=1600&height=700&seq=about-hero&orientation=landscape"
            alt="关于韩漫悦读品牌介绍"
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        </div>
        <div className="relative mx-auto flex w-full max-w-5xl flex-col items-center justify-center px-4 py-20 text-center md:py-28">
          <h1 className="max-w-2xl font-display text-3xl leading-tight text-background-50 md:text-5xl md:leading-tight">
            关于<span className="text-secondary-300">韩漫悦读</span>
          </h1>
          <p className="mt-5 max-w-2xl text-sm leading-7 text-background-100 md:text-base">
            一个专注免费韩国漫画聚合与推荐的阅读平台。我们致力于让每一位读者都能轻松找到、读懂、追完自己心仪的好漫画。
          </p>
        </div>
      </section>

      {/* Intro & positioning */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-primary-500">Brand Story</p>
            <h2 className="mt-3 font-display text-2xl text-foreground-950 md:text-3xl">品牌介绍</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              韩漫悦读诞生于对韩国漫画的热爱。我们观察到，面对海量的韩漫作品，许多读者常常感到无从下手——不知道哪部值得看、
              哪部已完结、哪种题材更合口味。
            </p>
            <p className="mt-4 text-sm leading-7 text-foreground-700">
              于是我们打造了这个聚合阅读站，把排行榜、完结推荐和恋爱、奇幻、校园、都市等题材分类整合到一起，
              让「找漫画」这件小事变得简单而愉悦。
            </p>
          </div>
          <div className="flex items-center overflow-hidden rounded-2xl border border-background-200/70">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20illustration%20of%20a%20small%20team%20of%20people%20reading%20and%20curating%20comic%20books%20around%20a%20table%2C%20warm%20peach%20and%20cream%20tones%2C%20cozy%20friendly%20atmosphere%2C%20clean%20background&width=800&height=600&seq=about-brand&orientation=landscape"
              alt="韩漫悦读团队选漫画场景"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-primary-500">Our Principles</p>
            <h2 className="mt-3 font-display text-2xl text-foreground-950 md:text-3xl">我们的内容原则</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-600">以严谨与热爱做推荐，把最值得读的韩漫送到你面前。</p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p) => (
              <div key={p.title} className="rounded-2xl border border-background-200/70 bg-background-50 p-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-primary-500">Voices</p>
          <h2 className="mt-3 font-display text-2xl text-foreground-950 md:text-3xl">读者口碑</h2>
          <p className="mt-3 text-sm leading-6 text-foreground-600">来自读者们的真实反馈，是我们不断前行的动力。</p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-2xl border border-background-200/70 bg-background-100 p-6">
              <div className="flex items-center gap-1 text-secondary-500">
                {Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <blockquote className="mt-4 text-sm leading-6 text-foreground-700">「{t.text}」</blockquote>
              <figcaption className="mt-5 flex items-center gap-3">
                <img src={t.avatar} alt={`${t.name} 读者头像`} className="h-10 w-10 rounded-full object-cover" />
                <span className="text-sm font-bold text-foreground-900">{t.name}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-primary-500">Contact</p>
              <h2 className="mt-3 font-display text-2xl text-foreground-950 md:text-3xl">联系我们</h2>
              <p className="mt-4 text-sm leading-7 text-foreground-600">
                有任何关于漫画推荐、题材分类或榜单的建议，欢迎通过下面的表单或邮箱与我们联系。你的每一个反馈，都会帮助我们做得更好。
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">邮箱</p>
                    <a href="mailto:contact@hanmanhub.example.com" className="text-sm font-medium text-foreground-800 hover:text-primary-500" rel="nofollow">
                      contact@hanmanhub.example.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                    <i className="ri-time-line"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">工作时间</p>
                    <p className="text-sm font-medium text-foreground-800">周一至周日 · 09:00 - 22:00</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
              <h3 className="font-display text-xl text-foreground-950">给我们留言</h3>
              <p className="mt-1 text-xs text-foreground-500">我们会尽快查看并回复你的留言。</p>
              <form data-readdy-form className="mt-6 space-y-4" onSubmit={onSubmit}>
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-name" className="mb-1.5 block text-xs font-medium text-foreground-700">你的称呼</label>
                    <input
                      id="c-name"
                      name="name"
                      type="text"
                      required
                      placeholder="请输入称呼"
                      className="h-11 w-full rounded-lg border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label htmlFor="c-email" className="mb-1.5 block text-xs font-medium text-foreground-700">邮箱</label>
                    <input
                      id="c-email"
                      name="email"
                      type="email"
                      required
                      placeholder="请输入邮箱"
                      className="h-11 w-full rounded-lg border border-background-300 bg-background-50 px-3.5 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="c-msg" className="mb-1.5 block text-xs font-medium text-foreground-700">留言内容</label>
                  <textarea
                    id="c-msg"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="请写下你的建议或想看的漫画……"
                    className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-3 text-sm text-foreground-800 outline-none placeholder:text-foreground-400 focus:border-primary-400"
                  ></textarea>
                  <p className="mt-1 text-right text-[10px] text-foreground-400">最多 500 字</p>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="w-full whitespace-nowrap rounded-full bg-primary-500 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? "提交中…" : "提交留言"}
                </button>
                {formStatus.type === "success" && (
                  <p className="flex items-center gap-1.5 rounded-lg bg-accent-100 px-4 py-2.5 text-sm text-accent-800">
                    <i className="ri-checkbox-circle-line"></i>
                    {formStatus.msg}
                  </p>
                )}
                {formStatus.type === "error" && (
                  <p className="flex items-center gap-1.5 rounded-lg bg-primary-100 px-4 py-2.5 text-sm text-primary-800">
                    <i className="ri-error-warning-line"></i>
                    {formStatus.msg}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}